import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { piBuilder } from '../../../shared/converter/entityBuilder';
import { useProjectDetailState } from '../../../shared/hooks/useApi/useProjectDetail';
import { useProjectInfoActions, useProjectInfoState } from '../../../shared/hooks/useApi/useProjectInfo';
import ProjectInfoMobile from './ProjectInfoMobile';
import Space from '../../../shared/Space';
import { useParams } from 'react-router-dom';
import { ModalType } from '../../../shared/enum/EnumRepository';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import reorderIcon from '../../../asset/icon/reorder.png';
import createInfoIcon from '../../../asset/icon/projectInfo_icon.png';
import confirmIcon from '../../../asset/icon/confirm2.png';
import confirmIcon2 from '../../../asset/icon/confirm3.png';
import HintWrapper from '../../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../../shared/constants/hintMessages';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { usePageOwnerIdentityStore } from '../../../shared/store/pageOwnerIdentityStore';
import { isPageOwnerMatchedToRaw } from '../../../shared/util/pageOwnerMatch';
import { useIsPageOwner } from '../../../shared/hooks/useIsPageOwner';
import { canShowProjectInfoList } from '../../../shared/util/visibility/projectDetailVisibility';
import type { ResourceScope } from '../../../shared/util/scopedResource';


const ProjectInfoListMobile: React.FC = () => {
  const { HANDLE: raw, projectId } = useParams<{ HANDLE?: string; projectId: string }>();
  const isOwner = useIsPageOwner();
  const { isEditMode } = useEditMode();
  const { project } = useProjectDetailState();
  const {
    projectInfoList,
    listOwnerAui,
    listScope,
    listProjectId,
    status,
  } = useProjectInfoState();
  const { getProjectInfoList, getProjectInfoListOwner, createProjectInfo, updateChangedProjectInfoBulk } = useProjectInfoActions();
  const hasChangedInfo = projectInfoList.some((pi) => pi.hasChanged);
  const { setStandardModal } = useModalStore();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowList = canShowProjectInfoList({
    pageOwnerAui: pageOwner?.aui,
    resourceOwnerAui: listOwnerAui,
    resourceScope: listScope,
    currentScope,
    status,
    resourceProjectId: listProjectId,
    currentProjectId: projectId,
  });

  useEffect(() => {
    if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;
    if (!project || !projectId) return;
    if (projectId !== project.id.toString()) return;

    const fetchProjectInfo = async () => {
      console.log("getting projectInfoList... projectId: ", project.id);
      if (isOwner) {
        await getProjectInfoListOwner(project.id);
      } else {
        await getProjectInfoList(project.id);
      }
    };

    fetchProjectInfo();
  }, [raw, pageOwner?.aui, projectId, project?.id, isOwner]);

  if (!canShowList || project == null) return null;

  const handleCreateInfo = async () => {
    if (!project) return;
    const newIndex = projectInfoList.length;
    await createProjectInfo(piBuilder(project.id, newIndex));
  };

  const handleUpdateChanged = async () => {
    if (!project) return;
    await updateChangedProjectInfoBulk(project.id);
  };

  const handleReOrder = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: "Info",
      value: null,
      handleChange: () => { },
    });
  }

  return (
    <ProjectInfoListComp>
      {projectInfoList.map((each) =>
        <ProjectInfoMobile key={each.id} data={each} />
      )}
      <Space $align={"center"} $height={"calc(6vw)"}>
        {isEditMode &&
          <BtnContainer data-has-changed={hasChangedInfo ? "true" : "false"}>
            {hasChangedInfo &&
              <HintWrapper content={getHintMessage('UPDATE_INFO')}>
                <UniversalIconBtn
                  isPrimary={true}
                  size="standard"
                  border={true}
                  backgroundImg={confirmIcon}
                  backgroundImgHover={confirmIcon2}
                  onClick={handleUpdateChanged} />
              </HintWrapper>
            }
            <HintWrapper content={getHintMessage('CREATE_NEW_INFO')}>
            <UniversalIconBtn 
                size="standard" 
                border={true} 
                backgroundImg={createInfoIcon} 
                onClick={handleCreateInfo} />
            </HintWrapper>
            <HintWrapper content={getHintMessage('REORDER_INFO')}>
            <UniversalIconBtn 
                size="standard" 
                border={true} 
                backgroundImg={reorderIcon} 
                onClick={handleReOrder} />
            </HintWrapper>
          </BtnContainer>
        }
      </Space>
    </ProjectInfoListComp>
  );
}

const ProjectInfoListComp = styled.article`
  position: relative;
  padding: 0 clamp(18px, 5vw, 24px);
  padding-bottom: calc(4vh);
`;

const BtnContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 2vh;
  gap: 0.5vh;
`

export default ProjectInfoListMobile;
